import React from 'react';
import { Box, Paper, Typography } from '@mui/material';

import {
  DIFFICULTY_LABELS,
  DifficultyLevel,
  MANNER_LABELS,
  TALKATIVENESS_LABELS,
} from '../../constants/trainingConstants';
import type { AiCustomerProfile } from './types';

const COPY = {
  title: 'The AI customer',
  manner: 'Manner',
  difficulty: 'Difficulty',
  talkativeness: 'How much they talk',
} as const;

const labelOf = (labels: Record<string, string>, value?: string): string =>
  (value && (labels[value] ?? value)) || '';

const Fact: React.FC<{ label: string; value: string; testId: string }> = ({ label, value, testId }) => (
  <Box data-testid={testId}>
    <Typography variant="caption" color="text.secondary" component="div">{label}</Typography>
    <Typography variant="body2">{value}</Typography>
  </Box>
);

/**
 * Who the rep faced in a Playbook Live session: the customer's role, manner,
 * difficulty (how hard it was to convince, and the grading bar) and how much
 * it talked. Shown above the score so the score reads in context. Renders
 * nothing for a knowledge base session or one analysed before this existed.
 */
export const AiCustomerContext: React.FC<{ profile?: AiCustomerProfile | null }> = ({ profile }) => {
  if (!profile) return null;
  const difficulty = labelOf(DIFFICULTY_LABELS as Record<string, string>, profile?.difficulty || DifficultyLevel.MEDIUM);
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="insights-ai-customer">
      <Typography variant="subtitle2" gutterBottom>{COPY.title}</Typography>
      {profile?.customerRole && (
        <Typography variant="body2" sx={{ mb: 1.5 }} data-testid="insights-ai-customer-role">
          {profile.customerRole}
        </Typography>
      )}
      <Box display="flex" flexWrap="wrap" gap={3}>
        <Fact label={COPY.manner} value={labelOf(MANNER_LABELS, profile?.manner)} testId="insights-ai-customer-manner" />
        <Fact label={COPY.difficulty} value={difficulty} testId="insights-ai-customer-difficulty" />
        <Fact
          label={COPY.talkativeness}
          value={labelOf(TALKATIVENESS_LABELS, profile?.talkativeness)}
          testId="insights-ai-customer-talkativeness"
        />
      </Box>
    </Paper>
  );
};

export default AiCustomerContext;
